import { isEmpty, pickBy } from 'lodash-es';
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import qs from 'qs';

export type Filters = Record<string, any>;

export function useFilters<F extends Filters>(options?: {
    ignoreQueryParams?: boolean;
    initialValues?: F;
}) {
    const router = useRouter();
    const route = useRoute();
    const appliedFilters = ref<F>(options?.initialValues || ({} as F));

    function resetFilters() {
        appliedFilters.value = {} as F;
    }

    function setFilter<K extends keyof F>(key: K, value: F[K] | undefined) {
        if (value !== undefined) {
            appliedFilters.value[key] = value;
        } else {
            delete appliedFilters.value[key];
        }
    }

    if (!options?.ignoreQueryParams) {
        watch(
            () => appliedFilters,
            () => {
                const nonEmptyFilters = pickBy(
                    appliedFilters.value,
                    (value) => value !== '' && value != null,
                );

                router?.replace({
                    query: {
                        ...route?.query,
                        filter: isEmpty(nonEmptyFilters)
                            ? undefined
                            : qs.stringify(nonEmptyFilters),
                    },
                });
            },
            { deep: true },
        );
    }

    // Parse URL query params on initial load
    function parseQueryFilters() {
        const filterQuery = route?.query.filter;
        if (!filterQuery || typeof filterQuery !== 'string') return;

        try {
            const parsedFilters = qs.parse(filterQuery);
            appliedFilters.value = parsedFilters as F;
        } catch {
            appliedFilters.value = {} as F;
        }
    }

    if (!options?.ignoreQueryParams) {
        parseQueryFilters();
    }

    const ctx = {
        appliedFilters,
        resetFilters,
        setFilter,
    };

    return ctx;
}
